import React from 'react';
import { DollarSign, Check } from 'lucide-react';
import { RainIcon } from './icons/OnTheRouteIcons';
import type { OSRMRoute, UnitsMode, AvoidOptions } from '../types';
import { formatDuration, formatDistance, formatClockTime } from '../utils/units';
import { getRouteTollInfo } from '../utils/tolls';

type RouteCardsProps = {
  routes: OSRMRoute[];
  selectedRouteIndex: number;
  onSelectRouteIndex: (index: number) => void;
  departureTime: Date;
  unitsMode: UnitsMode;
  isDark?: boolean;
  weatherSummary?: string;
  avoidOptions?: AvoidOptions;
};

export const RouteCards: React.FC<RouteCardsProps> = ({
  routes,
  selectedRouteIndex,
  onSelectRouteIndex,
  departureTime,
  unitsMode,
  isDark = false,
  weatherSummary,
  avoidOptions,
}) => {
  if (!routes || routes.length === 0) return null;

  // Identify the fastest route
  let fastestIdx = 0;
  let minDuration = routes[0]?.duration ?? Infinity;
  routes.forEach((r, idx) => {
    if (r.duration < minDuration) {
      minDuration = r.duration;
      fastestIdx = idx;
    }
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {routes.map((route, idx) => {
        const isSelected = selectedRouteIndex === idx;
        const isFastest = idx === fastestIdx;
        const arrivalTime = new Date(departureTime.getTime() + route.duration * 1000);
        const tollInfo = getRouteTollInfo(route);
        const routeSummary = route.legs?.[0]?.summary || (idx === 0 ? 'Primary Route' : `Alternative ${idx}`);
        
        // Custom weather note per route if alternative passes same rain zone
        const routeWeather =
          idx === 0
            ? weatherSummary || 'Light rain near Baker, 5:49-6:26 PM'
            : 'Also passes Baker in the rain';

        return (
          <div
            key={idx}
            className="card-surface"
            style={{
              padding: '12px 14px',
              borderRadius: 20,
              cursor: 'pointer',
              border: isSelected ? '2px solid var(--accent)' : '1px solid var(--sep)',
              backgroundColor: isSelected
                ? isDark
                  ? 'rgba(0, 122, 255, 0.12)'
                  : 'rgba(0, 122, 255, 0.06)'
                : undefined,
              boxShadow: isSelected ? '0 4px 16px rgba(0, 122, 255, 0.15)' : 'none',
              position: 'relative',
              transition: 'all 0.15s ease',
            }}
            onClick={() => onSelectRouteIndex(idx)}
            role="button"
            tabIndex={0}
            aria-label={`Select route ${routeSummary}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectRouteIndex(idx);
              }
            }}
          >
            {/* Header: Duration and Badges */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span className="t-title2" style={{ fontSize: 18, fontWeight: 700 }}>
                  {formatDuration(route.duration)}
                </span>
                {isSelected && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--accent)',
                    }}
                  >
                    <Check size={12} strokeWidth={3} /> Selected
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {isFastest && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 10,
                      backgroundColor: 'var(--accent)',
                      color: '#FFF',
                    }}
                  >
                    Fastest
                  </span>
                )}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 10,
                    backgroundColor: isDark ? 'rgba(52, 199, 89, 0.2)' : 'rgba(52, 199, 89, 0.15)',
                    color: '#34C759',
                    border: '1px solid rgba(52, 199, 89, 0.3)',
                  }}
                >
                  {tollInfo.badge}
                </span>
                {avoidOptions?.avoidTolls && tollInfo.badge === 'No tolls' && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: 8,
                      backgroundColor: 'var(--fill2)',
                      color: 'var(--accent)',
                    }}
                  >
                    Toll-free
                  </span>
                )}
              </div>
            </div>

            {/* Subtitle: Arrival • Distance • Route summary */}
            <div className="t-foot c2" style={{ marginBottom: 6, fontWeight: 500 }}>
              {formatClockTime(arrivalTime)} arrival • {formatDistance(route.distance, unitsMode).primary} • {routeSummary}
            </div>

            {/* Details: Weather and Toll notes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6, borderTop: '0.5px solid var(--sep)', paddingTop: 6 }}>
              {routeWeather && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--label2)' }}>
                  <RainIcon size={14} style={{ flexShrink: 0 }} />
                  <span>{routeWeather}</span>
                </div>
              )}
              {tollInfo.detail && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--label3)' }}>
                  <DollarSign size={13} style={{ flexShrink: 0 }} />
                  <span>{tollInfo.detail}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
